import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../contexts/UserProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { initResourceObj, logPaywallAction, uploadResourceToS3 } from '../misc/utils';
import { dbRefs, RESOURCES_UPLOAD_METHODS, PAYWALL_EVENTS, SEARCH_PARAMS } from '../misc/constants';
import axios from 'axios';
import { API } from '../misc/constants';
import AuthModal from '../modals/AuthModal2';
import SchoolGoat from '../assets/SchoolGoatBg.png';
import Brandy from '../assets/paywall1.webp';
import { useBreakpoint } from '../misc/useBreakpoint';
import { Pixel } from '../misc/analytics';
import { FileText, BookOpen, RefreshCw, Brain, PenTool, GraduationCap } from 'lucide-react';

const getPriceObj = async (paddle, user, priceId) => {
  if ( !paddle ) { console.log('no paddle'); return; }

  try {
    const result = await paddle.PricePreview({ items: [{ priceId: priceId, quantity: 1 }], address: { countryCode: 'US' } });
    const results = result.data;

    const outputObj = {
      totalUnformatted: Number((results?.details?.lineItems?.[0]?.totals?.total / 100).toFixed(2)),
      totalUnformattedNoDecimals: Number((results?.details?.lineItems?.[0]?.totals?.total / 100).toFixed(0)),
      totalFormatted: results?.details?.lineItems?.[0]?.formattedTotals?.total,
      currencySymbol: results?.details?.lineItems?.[0]?.formattedTotals?.total.slice(0, 1),
      interval: results?.details?.lineItems?.[0]?.price?.billingCycle?.interval,
      currencyCode: results?.currencyCode
    }

    return outputObj;
  }
  catch (error) {
    const outputObj = {
      totalUnformatted: 9.99,
      totalUnformattedNoDecimals: 9,
      totalFormatted: '$9.99',
      currencySymbol: '$',
      interval: 'month',
      currencyCode: 'USD'
    }

    return outputObj;
  }
}

export const DiscountOfferScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const [openAuthModal, setOpenAuthModal] = useState(false);
  const { isMobileAndBelow } = useBreakpoint();
  const [user_id, setUser_id] = useState(null);
  const [price_id, setPrice_id] = useState(null);
  const [priceDetails, setPriceDetails] = useState(null);
  const { paddle } = useContext(UserContext);
  const [ userObj, setUserObj ] = useState(null);

  useEffect(() => {
    // Parse query parameters
    const queryParams = new URLSearchParams(location.search);
    const userIdParam = queryParams.get('user_id');
    const priceIdParam = queryParams.get('price_id');

    if ( userIdParam && priceIdParam ) {
      setUser_id(userIdParam);
      setPrice_id(priceIdParam);
      
    }
    else {
      navigate('/');
    }

  }, [location.search]);


  useEffect(() => {
    if (user_id) {
      fetchUserData(user_id);
    }
  }, [user_id]);

  const fetchUserData = async (id) => {
    if (!id) return;
    
    try {
      let result = await axios.get(API + '/generalFindOne', { params: {
        queryObj: JSON.stringify({ _id: id }),
        dbRef: dbRefs.users
      }})
      
      let results = result.data
      setUserObj(results)
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };
  

  useEffect(() => {
    const fetchPriceDetails = async () => {
      if (userObj && price_id && paddle) {
        try {
          const priceObj = await getPriceObj(paddle, userObj, price_id);
          if (priceObj) {
            console.log('Price details fetched:', priceObj);
            setPriceDetails({
              ...priceObj,
              price: priceObj.interval !== "year" ? priceObj?.totalFormatted : `${priceObj?.currencySymbol || '$'}${(priceObj?.totalUnformatted / 12).toFixed(2)}`
            });
          }
        } catch (error) {
          console.error('Error fetching price details:', error);
        }
      }
    };

    fetchPriceDetails();
  }, [price_id, paddle, userObj]);

  const handlePlanClick = async () => {
    if (!paddle) return;

    try {
      await Promise.allSettled([
        logPaywallAction({ userObj, eventType: PAYWALL_EVENTS.CHECKOUT, type: 'paddle'}),
      ]);
    }
    catch (error) {
    }

    Pixel.track('InitiateCheckout');

    const checkoutOptions = {
      items: [ { priceId: price_id, quantity: 1 } ],
      
      settings: {
        theme: 'light',  
        successUrl: `${process.env.REACT_APP_FRONTEND_URL}/?${SEARCH_PARAMS.checkout_success}=${userObj?._id}&${SEARCH_PARAMS.payment_provider}=paddle`,
        variant: isMobileAndBelow ? "one-page" : "multi-page"
      },
      customer: {
        email: userObj?.email || '',
        address: {
          postalCode: userObj?.authData?.postal || '',
          countryCode: userObj?.authData?.country_code || '',
        }
      },
      discountId: "",
      customData: {
        mongo_id: userObj?._id,
        mongo_email: userObj?.email,
      }
      };

    paddle.Checkout.open(checkoutOptions);
  };


  const CONTAINER_STYLE = { display: 'flex', flexDirection: 'column', alignItems: 'center', height: isMobileAndBelow ? '100dvh' : '90dvh', width: isMobileAndBelow ? '100%' : '80%', maxWidth: '500px', margin: isMobileAndBelow ? '0' : 'auto', backgroundColor: '#fff', padding: '1rem', borderRadius: isMobileAndBelow ? '0' : '10px', boxShadow: isMobileAndBelow ? 'none' : '0 4px 8px rgba(0, 0, 0, 0.1)', position: 'relative', overflowY: "auto", overflowX: "hidden" };
  
  return (
    <div style={{ backgroundColor: "#f5f7fa", height: "100dvh", width: "100vw", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={CONTAINER_STYLE}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', cursor: 'pointer', width: '100%' }}
          onClick={() => navigate('/app/home')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#888888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </div>

        <h2 style={{ fontSize: '2.4rem', fontWeight: '600', textAlign: 'center', lineHeight: '1.3' }}>Your Personal Offer</h2>

        <div style={{ fontSize: '1.4rem', color: 'var(--color-text2)', textAlign: 'center', margin: '1rem 0', width: '100%' }}>
          <span>This is just for you!</span>
        </div>

        {/* Offer Section */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '400px', backgroundColor: '#f5f5f5', borderRadius: '1.5rem', padding: '3rem 1.5rem', position: 'relative', height: "100%",  animation: 'cardAppear 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards', opacity: 0, transform: 'scale(0.8)', marginTop: isMobileAndBelow ? '4rem' : '3rem' }}>
            <div style={{ position: 'absolute', top: isMobileAndBelow ? '-40px' : '-30px', width: isMobileAndBelow ? '80px' : '60px', height: isMobileAndBelow ? '80px' : '60px', borderRadius: '50%', backgroundColor: 'white', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)', border: '1px solid var(--color-backgroundTertiary)', animation: 'giftBounce 1s ease-out 0.5s forwards', opacity: 0, transform: 'translateY(-20px)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width={isMobileAndBelow ? 42 : 36} height={isMobileAndBelow ? 42 : 36} viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'giftPulse 2s infinite 1.5s' }}>
                <path d="M20 12v10H4V12"></path>
                <path d="M2 7h20v5H2z"></path>
                <path d="M12 22V7"></path>
                <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
              </svg>
            </div>
            
            <div style={{ width: '100%', textAlign: 'center', marginTop: '1rem', animation: 'fadeSlideUp 0.5s ease-out 0.9s forwards', opacity: 0, transform: 'translateY(15px)' }}>
              <span style={{ fontSize: '1.4rem' }}>Here's a <span style={{ backgroundColor: 'black', color: 'white', padding: '0.5rem 0.95rem', borderRadius: '20px', fontWeight: 'bold', animation: 'highlight 1s ease-out 1.5s' }}>massive</span> discount 🙌</span>
            </div>
            
            <div style={{ backgroundColor: 'white', width: '100%', padding: '1.5rem', borderRadius: '1rem', textAlign: 'center', margin: '1.5rem 0 0 0', animation: 'fadeSlideUp 0.5s ease-out 1.1s forwards', opacity: 0, transform: 'translateY(15px)', color: 'var(--color-text1)' }}>
              <span style={{ fontWeight: 'bold', fontSize: '1.4rem' }}>Only <span style={{ textDecoration: 'line-through', marginRight: '8px', color: 'var(--color-text4)', fontWeight: 'normal' }}>$19.99</span>{priceDetails?.price}/mo</span>
            </div>
            
            <span style={{ fontSize: '1rem', color: 'gray', marginTop: '1.5rem', animation: 'fadeSlideUp 0.5s ease-out 1.3s forwards', opacity: 0, transform: 'translateY(15px)', textAlign: 'center', lineHeight: '1.5' }}>Get the lowest price ever on your way to get<br/>the best grades of your life</span>
          </div>

          {/* Benefits Section */}
          <div style={{ width: '100%', maxWidth: '400px', marginTop: isMobileAndBelow ? '3rem' : '3rem', animation: 'fadeSlideUp 0.5s ease-out 1.4s forwards', opacity: 0, transform: 'translateY(15px)' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: '600', textAlign: 'center', marginBottom: '1rem' }}>What you'll get:</h3>
            <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
              {[
                { icon: FileText, text: 'Automatic note-taking in all your classes' },
                { icon: BookOpen, text: 'Practice exams and quizzes' },
                { icon: RefreshCw, text: 'Flashcards created from your notes' },
                { icon: Brain, text: 'Homework assistance and problem solving' },
                { icon: PenTool, text: 'Essay guidance and writing support' },
                { icon: GraduationCap, text: 'Personal tutoring 24/7' }
              ].map((item, index) => (
                <li key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.6rem', backgroundColor: 'white', padding: '0.8rem 1rem', borderRadius: '0.8rem', animation: `fadeSlideUp 0.5s ease-out ${1.4 + index * 0.1}s forwards`, opacity: 0, transform: 'translateY(10px)' }}>
                  {React.createElement(item.icon, { size: 20, color: 'var(--color-primary)', style: { marginRight: '12px' } })}
                  <span style={{ fontSize: '1rem', color: 'var(--color-text1)' }}>{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Testimonial Section */}
          <div style={{ width: '100%', maxWidth: '400px', marginTop: '2rem', backgroundColor: '#f8f9fa', padding: '1.5rem', borderRadius: '1rem', animation: 'fadeSlideUp 0.5s ease-out 1.9s forwards', opacity: 0, transform: 'translateY(15px)' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#e0e7ff', display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '12px' }}>
                <img src={Brandy} alt="Brandy" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>Brandy C.</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text3)', marginTop: '0.2rem' }}>Biology Major</div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex' }}>
                {[1, 2, 3, 4, 5].map(star => (
                  <span key={star} style={{ color: 'var(--color-primary)', fontSize: '1rem' }}>★</span>
                ))}
              </div>
            </div>
            <p style={{ fontSize: '1rem', fontStyle: 'italic', margin: 0, lineHeight: '1.5', color: 'var(--color-text2)' }}>
              "School Goat has completely transformed my grades. The automatic notes make it so I never miss anything in class. And I've never walked into my exams so confidently - the study tools make such a difference. I'm so glad I found this!"
            </p>
          </div>


          <button 
            onClick={handlePlanClick} 
            style={{ 
              padding: '1.25rem 2rem', 
              width: '100%',
              maxWidth: '400px',
              cursor: 'pointer', 
              borderRadius: '50px', 
              backgroundColor: '#000', 
              color: '#fff',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              marginTop: '2rem',
              animation: 'fadeSlideUp 0.5s ease-out 1.5s forwards',
              opacity: 0,
              transform: 'translateY(15px)'
            }}
          >
            Claim your limited offer now!
          </button>


        <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', pointerEvents: 'none', overflow: 'hidden', borderRadius: '16px' }}>
          {Array.from({ length: 500 }).map((_, i) => (
            <div key={i} style={{ 
              position: 'absolute', 
              width: '6px', 
              height: '6px', 
              backgroundColor: ['#a3e635', '#22d3ee', '#fcd34d'][i % 3], 
              borderRadius: '50%',
              top: `-10%`,
              left: `${Math.random() * 100}%`,
              animation: `fall${i % 3} ${0.8 + Math.random() * 1.2}s linear forwards`,
              animationDelay: `${Math.random() * 1.5}s`
            }}></div>
          ))}
        </div>

        <AuthModal open={openAuthModal} setOpen={setOpenAuthModal} />

        <style>
          {`
            @keyframes cardAppear {
              0% { opacity: 0; transform: scale(0.8); }
              100% { opacity: 1; transform: scale(1); }
            }
            @keyframes giftBounce {
              0% { opacity: 0; transform: translateY(-20px); }
              50% { opacity: 1; transform: translateY(10px); }
              75% { transform: translateY(-5px); }
              100% { opacity: 1; transform: translateY(0); }
            }
            @keyframes fadeSlideUp {
              0% { opacity: 0; transform: translateY(15px); }
              100% { opacity: 1; transform: translateY(0); }
            }
            @keyframes highlight {
              0% { transform: scale(1); }
              50% { transform: scale(1.1); box-shadow: 0 0 15px rgba(0,0,0,0.3); }
              100% { transform: scale(1); }
            }
            @keyframes fall0 {
              0% { transform: translateY(0) rotate(0deg); top: -10%; opacity: 1; }
              90% { opacity: 1; }
              100% { transform: translateY(110vh) rotate(360deg); top: 110%; opacity: 0; }
            }
            @keyframes fall1 {
              0% { transform: translateY(0) rotate(0deg); top: -10%; opacity: 1; }
              90% { opacity: 1; }
              100% { transform: translateY(110vh) rotate(-360deg); top: 110%; opacity: 0; }
            }
            @keyframes fall2 {
              0% { transform: translateY(0) rotate(0deg); top: -10%; opacity: 1; }
              90% { opacity: 1; }
              100% { transform: translateY(110vh) rotate(180deg); top: 110%; opacity: 0; }
            }
            @keyframes hideAfter4Seconds {
              0%, 99% { opacity: 1; }
              100% { opacity: 0; visibility: hidden; }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default DiscountOfferScreen;
